// ChallengeCard.tsx
import React from 'react';
import { Box, Card, Button, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import QuantitySelector from '../../components/QuantitySelector/QuantitySelector';
import classes from './_buyChallenge.module.scss';
import { ActivePromo } from './usePromoTimer';

interface Challenge {
  challengeBalance: number;
  initialPrice: number;
  currentPrice: number;
}

interface QuantityItem {
  quantity: number;
  price: number;
  originalPrice: number;
}

interface ChallengeCardProps {
  t: any;
  isDarkMode: boolean;
  challenge: Challenge;
  index: number;
  isLast: boolean;
  multiplierFactors: any;
  quantities: { [key: string]: QuantityItem };
  onQuantityChange: (productKey: string, newQuantity: number) => void;
  activePromo: ActivePromo;
  promoTimeLeft: number;
  formatTime: (ms: number) => string;
}

const ChallengeCard: React.FC<ChallengeCardProps> = ({
  t,
  isDarkMode,
  challenge,
  index,
  isLast,
  multiplierFactors,
  quantities,
  onQuantityChange,
  activePromo,
  promoTimeLeft,
  formatTime,
}) => {
  const productKey = `${challenge.challengeBalance}USD`;
  const quantity = quantities[productKey]?.quantity || 0;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 👉 Cyber Monday applicable sur 100K et 200K
  const isCyberMondayEligible =
    activePromo === 'cyberMonday' &&
    promoTimeLeft > 0 &&
    (challenge.challengeBalance === 100000 ||
      challenge.challengeBalance === 200000);

  const showCyberMonday = isCyberMondayEligible;

  const showBlackFriday =
    challenge.challengeBalance === 100000 &&
    activePromo === 'blackFriday' &&
    promoTimeLeft > 0;

  const FOUR_DAYS_MS = 4 * 24 * 60 * 60 * 1000;
  const showTimer = promoTimeLeft > 0 && promoTimeLeft < FOUR_DAYS_MS;

  const hasQuantity = quantity > 0;

  const hasPromoBadges = showCyberMonday || showBlackFriday;

  const effectiveCurrentPrice = isCyberMondayEligible
    ? challenge.challengeBalance === 200000
      ? 485
      : challenge.challengeBalance === 100000
      ? 249.5
      : challenge.currentPrice
    : challenge.currentPrice;

  const hasDiscount = effectiveCurrentPrice < challenge.initialPrice;

  return (
    <Card
      elevation={0}
      className={`${classes.card} ${isLast ? classes.lastCard : ''}`}
      sx={{
        position: 'relative',
        padding: '16px',
        // Sur mobile, on augmente le padding-top si une promo est affichée
        paddingTop: isMobile && hasPromoBadges ? '64px' : '16px',
      }}
    >
      {/* Badges promo */}
      {showCyberMonday && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: 5,
              left: 5,
              background: 'linear-gradient(45deg, #16a34a, #22c55e)',
              color: '#fff',
              padding: '10px 14px',
              borderRadius: '5px',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              zIndex: 2,
            }}
          >
            -51%
          </Box>

          {showTimer && (
            <Box
              sx={{
                position: 'absolute',
                top: 5,
                right: 5,
                background: 'linear-gradient(45deg, #16a34a, #22c55e)',
                color: '#fff',
                padding: '10px 14px',
                borderRadius: '5px',
                fontWeight: 'bold',
                fontSize: '0.9rem',
                zIndex: 2,
              }}
            >
              {formatTime(promoTimeLeft)}
            </Box>
          )}
        </>
      )}

      {showBlackFriday && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: 5,
              left: 5,
              background: 'linear-gradient(45deg, #16a34a, #22c55e)',
              color: '#fff',
              padding: '10px 14px',
              borderRadius: '5px',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              zIndex: 2,
            }}
          >
            {t('buyChallenge:promotions.BLACK_FRIDAY_BADGE')}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: 5,
              right: 5,
              background: 'linear-gradient(45deg, #16a34a, #22c55e)',
              color: '#fff',
              padding: '10px 14px',
              borderRadius: '5px',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              zIndex: 2,
            }}
          >
            {formatTime(promoTimeLeft)}
          </Box>
        </>
      )}

      {/* Titre */}
      <h2>
        {`${challenge?.challengeBalance.toLocaleString('fr-FR')} USD`}
      </h2>

      {/* Tableau des conditions */}
      <table
        className={`${classes.table} ${
          isDarkMode ? classes.dark : classes.light
        }`}
      >
        <thead className={classes.table__header}>
          <tr>
            <th></th>
            <th>{t('challengesCards.step')} 1</th>
            <th>{t('challengesCards.step')} 2</th>
          </tr>
        </thead>
        <tbody className={classes.table__body}>
          <tr>
            <td>{t('challengesCards.profitTarget')}</td>
            <td>
              {`${(
                challenge?.challengeBalance *
                multiplierFactors?.profitTargetPhase1
              ).toLocaleString('fr-FR')}$`}
            </td>
            <td>
              {`${(
                challenge?.challengeBalance *
                multiplierFactors?.profitTargetPhase2
              ).toLocaleString('fr-FR')}$`}
            </td>
          </tr>
          <tr>
            <td>{t('challengesCards.maxDailyLoss')}</td>
            <td>
              {`${(
                challenge?.challengeBalance *
                multiplierFactors?.maxDailyLoss
              ).toLocaleString('fr-FR')}$`}
            </td>
            <td>
              {`${(
                challenge?.challengeBalance *
                multiplierFactors?.maxDailyLoss
              ).toLocaleString('fr-FR')}$`}
            </td>
          </tr>
          <tr>
            <td>{t('challengesCards.maxLifetimeLoss')}</td>
            <td>
              {`${(
                challenge?.challengeBalance *
                multiplierFactors?.maxLifetimeLoss
              ).toLocaleString('fr-FR')}$`}
            </td>
            <td>
              {`${(
                challenge?.challengeBalance *
                multiplierFactors?.maxLifetimeLoss
              ).toLocaleString('fr-FR')}$`}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Footer : prix + actions */}
      <div className={classes.footer}>
        <div className={classes.pricesBlock}>
          <div className={classes.prices}>
            {hasDiscount && (
              <div
                className={`${classes.price} ${
                  isDarkMode ? classes.dark : classes.light
                }`}
              >
                {effectiveCurrentPrice}€
              </div>
            )}
            <div
              className={`${
                hasDiscount ? classes.strikePrice : classes.price
              } ${isDarkMode ? classes.dark : classes.light}`}
            >
              {`${challenge?.initialPrice} €`}
            </div>
          </div>
        </div>

        <div className={classes.actions}>
          {!hasQuantity ? (
            <Button
              variant="contained"
              fullWidth
              onClick={() => onQuantityChange(productKey, 1)}
            >
              {t('challengesCards.ADD_BUTTON')}
            </Button>
          ) : (
            <QuantitySelector
              initialQuantity={quantity}
              onQuantityChange={(newQuantity) =>
                onQuantityChange(productKey, newQuantity)
              }
            />
          )}
        </div>
      </div>
    </Card>
  );
};

export default ChallengeCard;
